import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Plus, Trash2 } from "lucide-react";
import { Chips, SectionCard, Segmented, Field, TextIn, toggleIn } from "./Controls";
import { MED_CONDITIONS, COND_STATUS, HABITS, NOTE_SHORTCUTS } from "./config";

function Conditions({ h, setH, slug }) {
  return (
    <SectionCard title="Medical conditions" subtitle="Unanswered conditions are recorded as 'Unknown'.">
      <div className="space-y-2.5">
        {MED_CONDITIONS[slug].map((c) => (
          <div key={c} className="flex flex-col gap-1.5 sm:flex-row sm:items-center sm:justify-between">
            <span className="text-sm text-[#101516] dark:text-white">{c}</span>
            <Segmented size="sm" testid={`cond-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`} value={h.conditions?.[c]} onChange={(v) => setH({ conditions: { ...h.conditions, [c]: v } })} options={COND_STATUS} />
          </div>
        ))}
        <Field label="Other condition"><TextIn value={h.condition_other} onChange={(v) => setH({ condition_other: v })} testid="cond-other" placeholder="Describe (optional)" /></Field>
      </div>
    </SectionCard>
  );
}

function RowList({ rows, cols, onChange, testid, addLabel }) {
  const set = (i, k, v) => onChange(rows.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2">
          {cols.map(([k, ph]) => <TextIn key={k} value={r[k]} onChange={(v) => set(i, k, v)} placeholder={ph} testid={`${testid}-${i}-${k}`} />)}
          <button type="button" aria-label="Remove row" onClick={() => onChange(rows.filter((_, j) => j !== i))} className="text-[#8B9698] hover:text-red-500" data-testid={`${testid}-${i}-remove`}><Trash2 className="h-4 w-4" /></button>
        </div>
      ))}
      <Button type="button" size="sm" variant="outline" onClick={() => onChange([...rows, Object.fromEntries(cols.map(([k]) => [k, ""]))])} data-testid={`${testid}-add`}><Plus className="mr-1 h-3.5 w-3.5" />{addLabel}</Button>
    </div>
  );
}

const Tick = ({ checked, onChange, label, testid }) => <label className="flex items-center gap-2 text-sm text-[#101516] dark:text-white"><Checkbox checked={!!checked} onCheckedChange={(v) => onChange(!!v)} data-testid={testid} />{label}</label>;

export default function StepHistory({ w, update, slug }) {
  const h = w.history;
  const setH = (patch) => update((d) => ({ ...d, history: { ...d.history, ...patch } }));
  const append = (text) => setH({ notes: `${h.notes ? h.notes.replace(/\s+$/, "") + "\n" : ""}• ${text}` });
  const al = h.allergies || { none: false, list: [] };
  const showPreg = w.patient?.gender !== "Male";
  return (
    <div className="space-y-5" data-testid="step-history">
      <Conditions h={h} setH={setH} slug={slug} />
      <SectionCard title="Current medications">
        <RowList rows={h.meds || []} cols={[["name", "Medicine"], ["dose", "Dose"], ["frequency", "Frequency"]]} onChange={(meds) => setH({ meds })} testid="hist-meds" addLabel="Add medicine" />
        {slug === "implant" && <div className="mt-3 flex flex-wrap gap-5"><Tick checked={h.antiresorptive} onChange={(v) => setH({ antiresorptive: v })} label="Antiresorptive / bisphosphonate" testid="hist-antiresorptive" /><Tick checked={h.anticoagulant} onChange={(v) => setH({ anticoagulant: v })} label="Anticoagulant / antiplatelet" testid="hist-anticoagulant" /></div>}
      </SectionCard>
      <SectionCard title="Allergies">
        <Tick checked={al.none} onChange={(v) => setH({ allergies: { none: v, list: v ? [] : al.list } })} label="No known allergies" testid="hist-allergy-none" />
        {!al.none && <div className="mt-3"><RowList rows={al.list || []} cols={[["substance", "Substance (e.g. Penicillin, NSAID)"], ["reaction", "Reaction"]]} onChange={(list) => setH({ allergies: { none: false, list } })} testid="hist-allergies" addLabel="Add allergy" /></div>}
      </SectionCard>
      <SectionCard title="Lifestyle">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
          {slug !== "tmj" && <Field label="Smoking / tobacco"><Segmented testid="hist-smoking" value={h.smoking} onChange={(v) => setH({ smoking: v })} options={["None", "Current", "Former", "Unknown"]} />{h.smoking === "Current" && <TextIn className="mt-2" value={h.smoking_per_day} onChange={(v) => setH({ smoking_per_day: v })} placeholder="Per day" testid="hist-smoking-per-day" />}</Field>}
          {showPreg && <Field label="Pregnancy"><Segmented testid="hist-pregnancy" value={h.pregnancy} onChange={(v) => setH({ pregnancy: v })} options={["Not applicable", "No", "Yes", "Unknown"]} /></Field>}
          {slug === "tmj" && <Field label="Habits"><Chips testid="hist-habits" options={HABITS} selected={h.habits || []} onToggle={(o) => setH({ habits: ["None", "Unknown"].includes(o) ? ((h.habits || []).includes(o) ? [] : [o]) : toggleIn((h.habits || []).filter((x) => !["None", "Unknown"].includes(x)), o) })} /></Field>}
        </div>
      </SectionCard>
      {slug === "implant" && (
        <SectionCard title="Patient expectations">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <Field label="Desired outcome"><Textarea rows={2} value={h.expectations?.desired} onChange={(e) => setH({ expectations: { ...h.expectations, desired: e.target.value } })} data-testid="hist-expect-desired" /></Field>
            <Field label="Current limitations"><Textarea rows={2} value={h.expectations?.limitations} onChange={(e) => setH({ expectations: { ...h.expectations, limitations: e.target.value } })} data-testid="hist-expect-limits" /></Field>
          </div>
        </SectionCard>
      )}
      <SectionCard title="Clinical notes" subtitle="Shortcuts add a new line to your notes — they never replace what you've written.">
        <div className="mb-3 space-y-2.5">
          {NOTE_SHORTCUTS.map((g) => (
            <div key={g.group} className="flex flex-wrap items-center gap-1.5">
              <span className="w-16 shrink-0 text-[10px] font-bold uppercase tracking-wider text-[#8B9698]">{g.group}</span>
              {g.items.map((it) => (
                <button key={it} type="button" onClick={() => append(it)} data-testid={`note-shortcut-${it.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                  className="inline-flex items-center gap-1 rounded-md border border-[#E2E8F0] px-2 py-1 text-[11px] font-semibold text-[#4E5F68] transition-colors hover:border-[#00A85A] hover:text-[#008F4D] dark:border-[#223139] dark:text-[#8B9698]"><Plus className="h-3 w-3" />{it}</button>
              ))}
            </div>
          ))}
        </div>
        <Textarea rows={6} value={h.notes} onChange={(e) => setH({ notes: e.target.value })} aria-label="Clinical notes" data-testid="clinical-notes" placeholder="Medical, dental and social history, examination remarks…" />
      </SectionCard>
    </div>
  );
}
